import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Tooltip,
  IconButton,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import AppTheme from "../../styles/AppTheme";
import CssBaseline from "@mui/material/CssBaseline";
import { brown, yellow } from "../../styles/ThemePrimitives";
import { useUser } from "../../contexts/UserContext";
import { useOutletContext } from "react-router-dom";
import { commitmentApiBase, yearFromOutletContext } from "../../util/commitmentHijriYear";

function formatDate(value) {
  if (!value) return "—";
  const s = String(value).trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  return s;
}

function normalizeListResponse(json) {
  const rows = Array.isArray(json?.data) ? json.data : [];
  const pagination = json?.pagination || {};
  return {
    rows,
    total: Number(pagination?.total ?? rows.length) || 0,
    page: Number(pagination?.current_page ?? 1) || 1,
    perPage: Number(pagination?.per_page ?? 20) || 20,
  };
}

function displayValue(value) {
  return value == null || value === "" ? "—" : value;
}

export default function ContributionReceipts() {
  const { token, loading: userLoading } = useUser();
  const { selectedYear } = useOutletContext() || {};
  const yearStr = yearFromOutletContext(selectedYear);

  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);

  const [viewOpen, setViewOpen] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState(null);

  const fetchReceipts = useCallback(async () => {
    if (!token || userLoading) return;
    if (!yearStr) {
      setRows([]);
      setTotal(0);
      setListLoading(false);
      return;
    }
    setListLoading(true);
    setListError(null);
    try {
      const body = {
        page: page + 1,
        per_page: perPage,
        year: yearStr,
      };
      if (search.trim()) body.search = search.trim();

      const res = await fetch(`${commitmentApiBase()}/commitment_receipt/retrieve`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      if (res.status === 401) {
        window.location.href = "/";
        return;
      }
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setListError(json?.message || "Could not load contribution receipts.");
        setRows([]);
        setTotal(0);
        return;
      }
      const { rows: nextRows, total: nextTotal, page: apiPage, perPage: apiPerPage } =
        normalizeListResponse(json);
      setRows(nextRows);
      setTotal(nextTotal);
      if (apiPage > 0) setPage(apiPage - 1);
      if (apiPerPage > 0 && apiPerPage !== perPage) setPerPage(apiPerPage);
    } catch (e) {
      setListError(e?.message || "Could not load contribution receipts.");
      setRows([]);
      setTotal(0);
    } finally {
      setListLoading(false);
    }
  }, [token, userLoading, search, yearStr, page, perPage]);

  useEffect(() => {
    document.title = "Contribution Receipts - FMB 52";
  }, []);

  useEffect(() => {
    fetchReceipts();
  }, [fetchReceipts]);

  const handleView = async (row) => {
    if (!row?.id || !token) return;
    setViewOpen(true);
    setViewLoading(true);
    setViewError(null);
    setSelectedReceipt(null);
    try {
      const res = await fetch(`${commitmentApiBase()}/commitment_receipt/retrieve/${row.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      if (res.status === 401) {
        window.location.href = "/";
        return;
      }
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        setViewError(json?.message || "Could not fetch receipt details.");
        return;
      }
      setSelectedReceipt(json && typeof json === "object" ? json : null);
    } catch (e) {
      setViewError(e?.message || "Could not fetch receipt details.");
    } finally {
      setViewLoading(false);
    }
  };

  const handlePrint = (row) => {
    if (!row?.id) return;
    window.open(`${commitmentApiBase()}/commitment_receipt_print/${row.id}`, "_blank", "noopener,noreferrer");
  };

  return (
    <AppTheme>
      <CssBaseline />
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          px: { xs: 2, sm: 3, md: 4 },
          pt: 3,
          pb: { xs: 3, sm: 4 },
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600, color: brown[800], mb: 2 }}>
          Contribution receipts
        </Typography>

        <Paper
          elevation={0}
          sx={{
            mb: 2,
            p: 2,
            border: "1px solid #F4EBD0",
            borderRadius: 2,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1.5, color: brown[800], fontWeight: 600 }}>
            Search
          </Typography>
          <TextField
            size="small"
            fullWidth
            label="Search"
            placeholder="Name, ITS, or receipt number"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
          />
        </Paper>

        {listError ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setListError(null)}>
            {listError}
          </Alert>
        ) : null}

        <Paper elevation={0} sx={{ border: "1px solid #F4EBD0", borderRadius: 2, overflow: "hidden" }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#faf6ef" }}>
                  <TableCell>Receipt No</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>ITS</TableCell>
                  <TableCell>Year</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Mode</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {listLoading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={32} />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                      No receipts found.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row, index) => (
                    <TableRow key={row.id ?? `${row.t_receipt_no || "receipt"}-${index}`} hover>
                      <TableCell>{displayValue(row.t_receipt_no)}</TableCell>
                      <TableCell>{displayValue(row.name)}</TableCell>
                      <TableCell>{displayValue(row.its)}</TableCell>
                      <TableCell>{displayValue(row.year)}</TableCell>
                      <TableCell>{formatDate(row.date)}</TableCell>
                      <TableCell>{displayValue(row.commitment?.type)}</TableCell>
                      <TableCell>{displayValue(row.mode)}</TableCell>
                      <TableCell align="right">{displayValue(row.amount)}</TableCell>
                      <TableCell>{displayValue(row.status)}</TableCell>
                      <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                        <Tooltip title="View receipt">
                          <IconButton size="small" onClick={() => handleView(row)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Print receipt">
                          <IconButton size="small" onClick={() => handlePrint(row)}>
                            <LocalPrintshopIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            rowsPerPage={perPage}
            onRowsPerPageChange={(e) => {
              setPerPage(Number(e.target.value) || 20);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </Paper>

        <Dialog open={viewOpen} onClose={() => !viewLoading && setViewOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Contribution receipt details</DialogTitle>
          <DialogContent>
            {viewLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : viewError ? (
              <Alert severity="error">{viewError}</Alert>
            ) : selectedReceipt ? (
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                {[
                  ["Receipt No", selectedReceipt.t_receipt_no],
                  ["Name", selectedReceipt.name],
                  ["ITS", selectedReceipt.its],
                  ["Year", selectedReceipt.year],
                  ["Date", formatDate(selectedReceipt.date)],
                  ["Commitment type", selectedReceipt.commitment?.type],
                  ["Mode", selectedReceipt.mode],
                  ["Amount", selectedReceipt.amount],
                  ["Status", selectedReceipt.status],
                  ["Comments", selectedReceipt.comments],
                  ["Bank", selectedReceipt.bank_name],
                  ["Cheque no.", selectedReceipt.cheque_no],
                  ["Transaction ID", selectedReceipt.transaction_id],
                ].map(([label, value]) => (
                  <Grid item xs={12} sm={6} key={label}>
                    <Paper elevation={0} sx={{ p: 1.5, border: "1px solid #F4EBD0", borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        {label}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {displayValue(value)}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No receipt details found.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            {selectedReceipt?.id ? (
              <Button
                variant="contained"
                onClick={() => handlePrint(selectedReceipt)}
                sx={{ backgroundColor: yellow[700], "&:hover": { backgroundColor: yellow[800] } }}
              >
                Print
              </Button>
            ) : null}
            <Button onClick={() => setViewOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AppTheme>
  );
}
